import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogOut, Plus, Trash2, Edit2, FileText, Users, Handshake, Mail, BarChart3 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function AdminPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [news, setNews] = useState([]);
  const [team, setTeam] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editDialog, setEditDialog] = useState({ open: false, type: "", data: null });

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const [s, n, t, p, c] = await Promise.all([
        ax.get("/stats"), ax.get("/news"), ax.get("/team"), ax.get("/partners"), ax.get("/contact")
      ]);
      setStats(s.data); setNews(n.data); setTeam(t.data); setPartners(p.data); setContacts(c.data);
    } catch (_) {
      // Auth expired or network error — UI keeps last-known state
    }
  }, []);

  useEffect(() => { if (user) fetchAll(); }, [user, fetchAll]);

  const handleDelete = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    await ax.delete(`/${type}/${id}`);
    fetchAll();
  };

  const handleSave = async (type, data, id) => {
    if (id) await ax.put(`/${type}/${id}`, data);
    else await ax.post(`/${type}`, data);
    setEditDialog({ open: false, type: "", data: null });
    fetchAll();
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#9CA3AF]">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F4F6]" data-testid="admin-page">
      <header className="border-b border-[#1F2937] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="shrink-0"><img src="https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/718pfmf4_New%20Company%20Logo.svg" alt="RoLink" className="h-7" /></a>
          <Badge variant="outline" className="text-[#9CA3AF] border-[#1F2937] text-xs">Admin</Badge>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-[#9CA3AF] hidden sm:inline">{user.email}</span>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="text-[#9CA3AF] hover:text-white">
            <LogOut size={16} />
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "News", value: stats.news || 0, icon: FileText, color: "#EE1D25" },
            { label: "Team", value: stats.team || 0, icon: Users, color: "#10B981" },
            { label: "Partners", value: stats.partners || 0, icon: Handshake, color: "#FFB000" },
            { label: "Inquiries", value: stats.contacts || 0, icon: Mail, color: "#EF4444" },
          ].map(s => (
            <div key={s.label} className="bg-[#111111] border border-[#1F2937] p-5" data-testid={`stat-${s.label.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2">
                <s.icon size={16} style={{ color: s.color }} />
                <span className="text-xs text-[#9CA3AF] uppercase tracking-wider">{s.label}</span>
              </div>
              <p className="font-heading text-3xl font-bold" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="news" data-testid="admin-tabs">
          <TabsList className="bg-[#111111] border border-[#1F2937] rounded-none h-auto p-1 mb-6">
            {["news", "team", "partners", "contacts"].map(tab => (
              <TabsTrigger key={tab} value={tab} className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-[#EE1D25] data-[state=active]:text-white px-4 py-2">
                {tab}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="news">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-lg font-semibold">News & Publications</h2>
              <Button size="sm" className="bg-[#EE1D25] rounded-none" onClick={() => setEditDialog({ open: true, type: "news", data: null })} data-testid="add-news-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {news.map(n => (
                <div key={n.id} className="flex items-center justify-between bg-[#111111] border border-[#1F2937] p-4" data-testid={`news-item-${n.id}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-[#9CA3AF] border-[#1F2937] text-xs">{n.category}</Badge>
                      <span className="text-xs text-[#4B5563]">{n.date}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditDialog({ open: true, type: "news", data: n })}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete("news", n.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="team">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-lg font-semibold">Team Members</h2>
              <Button size="sm" className="bg-[#EE1D25] rounded-none" onClick={() => setEditDialog({ open: true, type: "team", data: null })} data-testid="add-team-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {team.map(t => (
                <div key={t.id} className="flex items-center justify-between bg-[#111111] border border-[#1F2937] p-4" data-testid={`team-item-${t.id}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium">{t.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{t.role} — {t.expertise}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditDialog({ open: true, type: "team", data: t })}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete("team", t.id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="partners">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-lg font-semibold">Partners</h2>
              <Button size="sm" className="bg-[#EE1D25] rounded-none" onClick={() => setEditDialog({ open: true, type: "partners", data: null })} data-testid="add-partner-btn">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {partners.map(p => (
                <div key={p.id} className="flex items-center justify-between bg-[#111111] border border-[#1F2937] p-4" data-testid={`partner-item-${p.id}`}>
                  <div className="flex-1 min-w-0 mr-4">
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{p.category}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-red-400" onClick={() => handleDelete("partners", p.id)}><Trash2 size={14} /></Button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="contacts">
            <h2 className="font-heading text-lg font-semibold mb-4">Contact Submissions</h2>
            <div className="space-y-2">
              {contacts.map(c => (
                <div key={c.id} className="bg-[#111111] border border-[#1F2937] p-4" data-testid={`contact-item-${c.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <p className="text-sm font-medium">{c.name}</p>
                    <Badge variant="outline" className="text-xs border-[#1F2937]">{c.inquiry_type}</Badge>
                    <Badge variant="outline" className={`text-xs ${c.status === 'new' ? 'border-[#EE1D25] text-[#EE1D25]' : 'border-[#1F2937] text-[#9CA3AF]'}`}>{c.status}</Badge>
                  </div>
                  <p className="text-xs text-[#9CA3AF]">{c.email} {c.organization && `— ${c.organization}`}</p>
                  <p className="text-sm text-[#4B5563] mt-2">{c.message}</p>
                  <p className="text-xs text-[#374151] mt-2">{new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-sm text-[#4B5563] text-center py-8">No submissions yet</p>}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <EditDialog dialog={editDialog} onClose={() => setEditDialog({ open: false, type: "", data: null })} onSave={handleSave} />
    </div>
  );
}

function EditDialog({ dialog, onClose, onSave }) {
  const { open, type, data } = dialog;
  const isEdit = !!data;
  const [form, setForm] = useState({});

  useEffect(() => {
    if (type === "news") setForm(data || { title: "", category: "", summary: "", content: "", date: "" });
    else if (type === "team") setForm(data || { name: "", role: "", expertise: "", bio: "", image_url: "" });
    else if (type === "partners") setForm(data || { name: "", logo_url: "", category: "" });
  }, [type, data]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...form };
    delete payload.id;
    delete payload.created_at;
    onSave(type, payload, data?.id);
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="bg-[#050505] border-[#1F2937] max-w-lg" data-testid="edit-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#F3F4F6] font-heading">{isEdit ? "Edit" : "Add"} {type}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          {type === "news" && (
            <>
              <div><Label className="text-xs text-[#9CA3AF]">Title</Label><Input value={form.title || ""} onChange={e => set("title", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#9CA3AF]">Category</Label><Input value={form.category || ""} onChange={e => set("category", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
                <div><Label className="text-xs text-[#9CA3AF]">Date</Label><Input type="date" value={form.date || ""} onChange={e => set("date", e.target.value)} className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              </div>
              <div><Label className="text-xs text-[#9CA3AF]">Summary</Label><Textarea value={form.summary || ""} onChange={e => set("summary", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" rows={3} /></div>
            </>
          )}
          {type === "team" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-[#9CA3AF]">Name</Label><Input value={form.name || ""} onChange={e => set("name", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
                <div><Label className="text-xs text-[#9CA3AF]">Role</Label><Input value={form.role || ""} onChange={e => set("role", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              </div>
              <div><Label className="text-xs text-[#9CA3AF]">Expertise</Label><Input value={form.expertise || ""} onChange={e => set("expertise", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              <div><Label className="text-xs text-[#9CA3AF]">Bio</Label><Textarea value={form.bio || ""} onChange={e => set("bio", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" rows={3} /></div>
              <div><Label className="text-xs text-[#9CA3AF]">Image URL</Label><Input value={form.image_url || ""} onChange={e => set("image_url", e.target.value)} className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
            </>
          )}
          {type === "partners" && (
            <>
              <div><Label className="text-xs text-[#9CA3AF]">Name</Label><Input value={form.name || ""} onChange={e => set("name", e.target.value)} required className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              <div><Label className="text-xs text-[#9CA3AF]">Category</Label><Input value={form.category || ""} onChange={e => set("category", e.target.value)} className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
              <div><Label className="text-xs text-[#9CA3AF]">Logo URL</Label><Input value={form.logo_url || ""} onChange={e => set("logo_url", e.target.value)} className="bg-[#111111] border-[#1F2937] text-white rounded-none mt-1" /></div>
            </>
          )}
          <div className="flex gap-2 pt-2">
            <Button type="submit" className="bg-[#EE1D25] rounded-none flex-1" data-testid="edit-save-btn">{isEdit ? "Update" : "Create"}</Button>
            <Button type="button" variant="outline" className="rounded-none border-[#1F2937] text-[#9CA3AF]" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
